import { React, useEffect, useState } from "react";
import KeyboardArrowRightIcon from "@mui/icons-material/ArrowForwardIos";
import MDTypography from "components/MDTypography";
import { TextField } from "@mui/material";
import PropTypes from "prop-types";

const navSections = [
  { id: "introduction", targets: ["introduction"] },
  { id: "getting-started", targets: ["getting-started"] },
  { id: "api-calls", targets: ["api-calls"] },
  { id: "login-api", targets: ["login-api"] },
  { id: "login-request-body", targets: ["login-request-body"] },
  { id: "login-request-body-table", targets: ["login-request-body-table"] },
  { id: "login-response", targets: ["login-response"] },
  { id: "get-events-api", targets: ["get-events-api"] },
  { id: "get-events-query-params", targets: ["get-events-query-params"] },
  { id: "column-filter-mapping", targets: ["column-filter-mapping"] },
  { id: "event-types-mapping", targets: ["event-types-mapping"] },
  { id: "response-object", targets: ["response-object"] },
  { id: "response-messages", targets: ["response-messages"] },
];

function MultiSelectTreeView(props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeNavId, setActiveNavId] = useState("introduction");

  useEffect(() => {
    const toggler = document.getElementsByClassName("caret");
    const togglerArray = Array.from(toggler);
    const clickHandlers = [];

    for (const element of togglerArray) {
      const handleCaretClick = (event) => {
        const nested =
          event.currentTarget.parentElement.querySelector(".nested");
        if (nested) {
          nested.classList.toggle("active");
        }
        event.currentTarget.classList.toggle("caret-down");
      };

      element.addEventListener("click", handleCaretClick);
      clickHandlers.push({ element, handleCaretClick });
    }

    return () => {
      clickHandlers.forEach(({ element, handleCaretClick }) => {
        element.removeEventListener("click", handleCaretClick);
      });
    };
  }, []);

  useEffect(() => {
    const scrollContainer = props.scrollRef?.current;
    if (!scrollContainer) return undefined;

    const syncActiveSection = () => {
      const containerTop = scrollContainer.getBoundingClientRect().top;
      let nextActiveNavId = navSections[0].id;

      if (scrollContainer.scrollTop <= 0) {
        setActiveNavId((cur) => (cur === nextActiveNavId ? cur : nextActiveNavId));
        return;
      }

      navSections.forEach(({ id, targets }) => {
        const el = targets.map((t) => document.getElementById(t)).find(Boolean);
        if (!el) return;
        const top = el.getBoundingClientRect().top - containerTop;
        if (top <= 140) nextActiveNavId = id;
      });

      setActiveNavId((cur) => (cur === nextActiveNavId ? cur : nextActiveNavId));
    };

    syncActiveSection();
    scrollContainer.addEventListener("scroll", syncActiveSection, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", syncActiveSection);
  }, [props.scrollRef]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    props.searchedData(e.target.value);
  };

  const activeNavClass = (id) => (activeNavId === id ? "ss-actives" : "");

  return (
    <>
      <TextField
        className="outlined-basic-subject search-doc my-2"
        placeholder="Search docs..."
        value={searchTerm}
        onChange={handleInputChange}
        autoComplete="off"
      />
      {props.titleNode}
      <MDTypography
        variant="p"
        fontSize="13px"
        fontWeight="regular"
        fontFamily="inherit"
        color="white"
      >
        <ul className="docs-nav">
          <li>
            <a href="#introduction">
              <MDTypography
                variant="h6"
                fontSize="13px"
                fontWeight="regular"
                fontFamily="inherit"
              >
                <div className={activeNavClass("introduction")} data-to-scrollspy-id="introduction">
                  Overview
                </div>
              </MDTypography>
            </a>
          </li>
          <li>
            <a href="#getting-started">
              <MDTypography
                variant="h6"
                fontSize="13px"
                fontWeight="regular"
                fontFamily="inherit"
              >
                <div className={activeNavClass("getting-started")} data-to-scrollspy-id="getting-started">
                  Getting Started
                </div>
              </MDTypography>
            </a>
          </li>
          <li>
            <span className="caret">
              <a href="#api-calls">
                <MDTypography
                  variant="h6"
                  fontSize="13px"
                  fontWeight="regular"
                  fontFamily="inherit"
                >
                  <div className={activeNavClass("api-calls")} data-to-scrollspy-id="api-calls">
                    API Calls
                    <KeyboardArrowRightIcon />
                  </div>
                </MDTypography>
              </a>
            </span>
            <ul className="nested">
              <li>
                <span className="caret">
                  <a href="#login-api">
                    <MDTypography
                      variant="h6"
                      fontSize="13px"
                      fontWeight="regular"
                      fontFamily="inherit"
                    >
                      <div className={activeNavClass("login-api")} data-to-scrollspy-id="login-api">
                        Login API
                        <KeyboardArrowRightIcon />
                      </div>
                    </MDTypography>
                  </a>
                </span>
                <ul className="nested">
                  <li>
                    <span className="caret">
                      <a href="#login-request-body">
                        <MDTypography
                          variant="h6"
                          fontSize="13px"
                          fontWeight="regular"
                          fontFamily="inherit"
                        >
                          <div
                            className={activeNavClass("login-request-body")}
                            data-to-scrollspy-id="login-request-body"
                          >
                            Request Parameters
                            <KeyboardArrowRightIcon />
                          </div>
                        </MDTypography>
                      </a>
                    </span>
                    <ul className="nested">
                      <li>
                        <a href="#login-request-body-table">
                          <span className="caret">
                            <MDTypography
                              variant="h6"
                              fontSize="13px"
                              fontWeight="regular"
                              fontFamily="inherit"
                            >
                              <div className={activeNavClass("login-request-body-table")} data-to-scrollspy-id="login-request-body-table">
                                Request Body
                              </div>
                            </MDTypography>
                          </span>
                        </a>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <span className="caret">
                      <a href="#login-response">
                        <MDTypography
                          variant="h6"
                          fontSize="13px"
                          fontWeight="regular"
                          fontFamily="inherit"
                        >
                          <div className={activeNavClass("login-response")} data-to-scrollspy-id="login-response">
                            Response
                          </div>
                        </MDTypography>
                      </a>
                    </span>
                  </li>
                </ul>
              </li>
              <li>
                <span className="caret">
                  <a href="#get-events-api">
                    <MDTypography
                      variant="h6"
                      fontSize="13px"
                      fontWeight="regular"
                      fontFamily="inherit"
                    >
                      <div className={activeNavClass("get-events-api")} data-to-scrollspy-id="get-events-api">
                        vFlux Events API
                        <KeyboardArrowRightIcon />
                      </div>
                    </MDTypography>
                  </a>
                </span>
                <ul className="nested">
                  <li>
                    <span className="caret">
                      <a
                        href="#get-events-query-params"
                      >
                        <MDTypography
                          variant="h6"
                          fontSize="13px"
                          fontWeight="regular"
                          fontFamily="inherit"
                        >
                          <div
                            className={activeNavClass("get-events-query-params")}
                            data-to-scrollspy-id="get-events-query-params"
                          >
                            Request Parameters
                            <KeyboardArrowRightIcon />
                          </div>
                        </MDTypography>
                      </a>
                    </span>
                    <ul className="nested">
                      <li>
                        <a href="#column-filter-mapping">
                          <span className="caret">
                            <MDTypography
                              variant="h6"
                              fontSize="13px"
                              fontWeight="regular"
                              fontFamily="inherit"
                            >
                              <div className={activeNavClass("column-filter-mapping")} data-to-scrollspy-id="column-filter-mapping">
                                Column Filter Type and ID Mapping
                              </div>
                            </MDTypography>
                          </span>
                        </a>
                      </li>
                      <li>
                        <a href="#event-types-mapping">
                          <span className="caret">
                            <MDTypography
                              variant="h6"
                              fontSize="13px"
                              fontWeight="regular"
                              fontFamily="inherit"
                            >
                              <div className={activeNavClass("event-types-mapping")} data-to-scrollspy-id="event-types-mapping">
                                Event types and ID Mapping
                              </div>
                            </MDTypography>
                          </span>
                        </a>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <span className="caret">
                      <a href="#response-messages">
                        <MDTypography
                          variant="h6"
                          fontSize="13px"
                          fontWeight="regular"
                          fontFamily="inherit"
                        >
                          <div
                            className={activeNavClass("response-object")}
                            data-to-scrollspy-id="response-object"
                          >
                            Response Object
                            <KeyboardArrowRightIcon />
                          </div>
                        </MDTypography>
                      </a>
                    </span>
                    <ul className="nested">
                      <li>
                        <a href="#response-messages">
                          <span className="caret">
                            <MDTypography
                              variant="h6"
                              fontSize="13px"
                              fontWeight="regular"
                              fontFamily="inherit"
                            >
                              <div className={activeNavClass("response-messages")} data-to-scrollspy-id="response-messages">
                                Response Messages / Types
                              </div>
                            </MDTypography>
                          </span>
                        </a>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </MDTypography>
    </>
  );
}
MultiSelectTreeView.propTypes = {
  searchedData: PropTypes.func.isRequired,
  scrollRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
};
export default MultiSelectTreeView;
